import type {
  BucketDeployment,
  BucketName,
  S3BucketMapping,
} from "@atoms/atom-types";

type S3ImportMetaEnv = {
  readonly S3_BUCKETS: string;
  readonly S3_FQDN: string;
};

declare global {
  interface ImportMetaEnv extends S3ImportMetaEnv {}

  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isS3BucketMapping(value: unknown): value is S3BucketMapping {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.bucketName === "string" &&
    value.bucketName.length > 0 &&
    typeof value.bucketDeployment === "string" &&
    value.bucketDeployment.length > 0 &&
    typeof value.public === "boolean"
  );
}

function parseS3BucketMappingsArray(raw: unknown[]): S3BucketMapping[] {
  return raw.every(isS3BucketMapping) ? raw : [];
}

/**
 * Parses `S3_BUCKETS` configuration into bucket mappings.
 *
 * Accepts JSON strings (runtime env) and pre-parsed arrays (compile-time
 * import-meta-env injection). Returns an empty array for missing or invalid input.
 */
export function parseS3BucketMappings(raw: unknown): S3BucketMapping[] {
  if (raw == null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return parseS3BucketMappingsArray(raw);
  }
  if (typeof raw !== "string") {
    return [];
  }

  const trimmed = raw.trim();
  if (!trimmed) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parseS3BucketMappingsArray(parsed) : [];
  } catch {
    return [];
  }
}

/**
 * Reads bucket mappings from the frontend `S3_BUCKETS` environment variable.
 */
export function readS3BucketMappingsFromEnv(): S3BucketMapping[] {
  return parseS3BucketMappings(import.meta.env.S3_BUCKETS);
}

/**
 * Reads the public S3 base URL from the frontend `S3_FQDN` environment variable.
 *
 * @returns The configured FQDN, or `undefined` when not set.
 */
export function readS3FqdnFromEnv(): string | undefined {
  const fqdn = import.meta.env.S3_FQDN;
  return typeof fqdn === "string" && fqdn.length > 0 ? fqdn : undefined;
}

/**
 * Resolves a semantic bucket name to its deployment-specific bucket name.
 *
 * Falls back to the semantic name when no mapping is found.
 */
export function resolveDeployedBucketName(
  semanticName: BucketName,
  mappings: S3BucketMapping[],
): BucketDeployment {
  const semantic = String(semanticName);
  const mapping = mappings.find(
    (entry) => String(entry.bucketName) === semantic,
  );
  return (mapping?.bucketDeployment ?? semantic) as BucketDeployment;
}

/**
 * Resolves a semantic bucket name for public browser URLs.
 *
 * Returns `undefined` when the bucket is explicitly mapped as private.
 */
export function resolvePublicDeployedBucketName(
  semanticName: BucketName,
  mappings: S3BucketMapping[],
): BucketDeployment | undefined {
  const semantic = String(semanticName);
  const mapping = mappings.find(
    (entry) => String(entry.bucketName) === semantic,
  );
  if (mapping?.public === false) {
    return undefined;
  }

  return (mapping?.bucketDeployment ?? semantic) as BucketDeployment;
}

function stripLeadingSlashes(value: string): string {
  let start = 0;
  while (start < value.length && value[start] === "/") {
    start += 1;
  }
  return value.slice(start);
}

export function encodeObjectKeyPath(objectKey: string): string {
  return stripLeadingSlashes(objectKey)
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
}

/**
 * Builds a public object URL from explicit FQDN and bucket mappings.
 *
 * @param s3Fqdn - Public base URL, e.g. `https://s3.example.com`
 * @param mappings - Semantic-to-deployment bucket mappings
 * @param bucket - Semantic bucket name used in application code
 * @param objectKey - Stored object key within the bucket
 * @returns A fully qualified public URL, or `undefined` when inputs are empty
 */
export function buildPublicFileUrlFromMappings(
  s3Fqdn: string,
  mappings: S3BucketMapping[],
  bucket: BucketName,
  objectKey: string,
): string | undefined {
  if (!s3Fqdn || !objectKey) {
    return undefined;
  }

  const actualBucket = resolvePublicDeployedBucketName(bucket, mappings);
  if (!actualBucket) {
    return undefined;
  }

  return `${s3Fqdn.replace(/\/$/, "")}/${actualBucket}/${encodeObjectKeyPath(objectKey)}`;
}
